import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/seo';
import { APP } from '@/constants/brand';
import { Wrench, Loader2, Mail, ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useSEO({
    title: 'Reset Password — DIY X-treme',
    description: "Forgot your DIY X-treme password? We'll email you a link to set a new one.",
    canonical: 'https://diybot.org/auth/forgot',
  });

  const submit = async (e) => {
    e.preventDefault();
    if (loading || sent) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 grid-bg relative">
      <button
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
        data-testid="forgot-back-btn"
        className="absolute top-4 left-4 inline-flex items-center gap-1 text-zinc-400 hover:text-amber-500 border border-zinc-800 hover:border-amber-500 px-3 py-2 font-mono text-xs uppercase tracking-widest bg-zinc-950/70 backdrop-blur transition-colors z-10"
        aria-label="Back"
      >
        <ArrowLeft size={14} /> <span>Back</span>
      </button>

      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-11 h-11 bg-amber-500 flex items-center justify-center">
            <Wrench size={22} className="text-zinc-950" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-display text-3xl text-zinc-100 tracking-wide leading-none">{APP.name}</h1>
            <div className="font-mono text-[10px] text-amber-500 uppercase tracking-[0.2em] mt-1">
              {APP.tagline}
            </div>
          </div>
        </div>

        <div className="industrial-card border-t-4 border-t-amber-400">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-2 flex items-center gap-2">
            <Mail size={12} /> Password reset
          </div>
          <h2 className="font-display text-3xl text-zinc-100 tracking-tight mb-4">Forgot your password?</h2>

          {!sent ? (
            <>
              <p className="text-zinc-400 text-sm mb-6">
                No sweat. Type the email on your account and we'll send you a link to set a new one.
              </p>
              <form onSubmit={submit} className="space-y-3">
                <input
                  data-testid="forgot-email"
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="industrial-input"
                  placeholder="you@example.com"
                />
                {error && (
                  <div data-testid="forgot-error" className="text-red-400 text-sm font-mono">{error}</div>
                )}
                <button
                  type="submit"
                  data-testid="forgot-submit"
                  disabled={loading || !email.trim()}
                  className="btn-primary w-full disabled:opacity-50"
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Email me a reset link <ArrowRight size={16} /></>}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-success" className="text-center py-4">
              <CheckCircle2 size={40} className="text-lime-400 mx-auto mb-3" />
              <div className="font-display text-2xl text-zinc-100 mb-2">Check your email</div>
              <p className="text-zinc-400 text-sm">
                If <span className="text-amber-400 font-mono">{email}</span> is on file, a reset link is on its way. It expires in 30 minutes.
              </p>
              <p className="text-zinc-500 text-xs mt-4 font-mono uppercase tracking-widest">
                Nothing in your inbox? Check spam · try again in 60 seconds
              </p>
            </div>
          )}

          <div className="mt-6 border-t border-zinc-800 pt-4 text-center">
            <Link to="/auth?mode=login" data-testid="forgot-back-to-login" className="text-zinc-400 hover:text-amber-500 text-sm font-mono uppercase tracking-widest">
              &larr; Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
